import { useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { EventSource } from "eventsource";

/**
 * Configuration for a single SSE event type
 */
export interface SSEEventConfig {
  /** The event type to listen for */
  eventType: string;
  /** Callback function to handle the event data */
  onEvent: (data: Record<string, any>, event: MessageEvent) => void;
}

/**
 * Configuration options for the SSE subscription
 */
export interface SSESubscriptionConfig {
  /** Base URL for the SSE endpoint */
  url: string;
  /** Optional service identifier used for logging, error messages, or distinguishing between SSE connections */
  service?: string;
  /** Optional event types to subscribe to via query params (event_types) */
  eventTypes?: string[];
  /** Array of event configurations to subscribe to */
  events: SSEEventConfig[];
  /** Optional error handler */
  onError?: (error: Event) => void;
  /** Optional connection opened handler */
  onOpen?: () => void;
  /** Additional custom headers to include in the request */
  customHeaders?: Record<string, string>;
  /** Whether the subscription is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Build SSE URL with event_types query parameters
 */
const buildUrlWithEventTypes = (baseUrl: string, types?: string[]): string => {
  if (!types || types.length === 0) return baseUrl;
  const hasQuery = baseUrl.includes("?");
  const prefix = hasQuery ? "&" : "?";
  const params = types
    .filter(Boolean)
    .map((t) => `event_types=${encodeURIComponent(t)}`)
    .join("&");
  return `${baseUrl}${prefix}${params}`;
};

/**
 * Custom React hook for subscribing to Server-Sent Events (SSE)
 *
 * @example
 * ```tsx
 * const { isConnected } = useSSESubscription({
 *   url: 'https://api.example.com/events',
 *   events: [
 *     {
 *       eventType: 'support_session',
 *       onEvent: (data) => {
 *         const parsed = JSON.parse(data);
 *         dispatch(updateSupportSession(parsed));
 *       }
 *     },
 *     {
 *       eventType: 'ping',
 *       onEvent: () => console.log('Ping received')
 *     }
 *   ],
 *   onError: (err) => console.error('SSE Error:', err),
 *   onOpen: () => console.log('SSE Connected')
 * });
 * ```
 *
 * @param config - SSE subscription configuration
 * @returns Object containing connection state
 */
export const useSSESubscription = (config: SSESubscriptionConfig) => {
  const {
    url,
    eventTypes,
    events,
    onError,
    onOpen,
    customHeaders = {},
    enabled = true,
    service,
  } = config;

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const eventSourceRef = useRef<EventSource | null>(null);
  const isConnectedRef = useRef<boolean>(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // memoize event type keys to avoid unnecessary re-renders
  const eventTypeKeys = useMemo(
    () => events.map((e) => e.eventType).join(","),
    [events],
  );
  const eventTypesKey = useMemo(
    () => (eventTypes || []).join(","),
    [eventTypes],
  );
  const customHeadersKey = useMemo(
    () => JSON.stringify(customHeaders),
    [customHeaders],
  );

  useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    if (!enabled) {
      console.log(`${service} SSE subscription disabled`);
      return;
    }

    if (!url) {
      console.error(`${service} SSE subscription skipped: no URL provided`);
      return;
    }

    // only connect if auth is ready
    if (!authContext?.accessToken || !authContext?.customerNamespace) {
      console.log(`${service} SSE subscription skipped: auth not ready`);
      return;
    }

    const headers: Record<string, string> = { ...customHeaders };
    if (authContext?.accessToken) {
      headers["Authorization"] = `Bearer ${authContext.accessToken}`;
    }

    const effectiveEventTypes =
      eventTypes && eventTypes.length > 0
        ? eventTypes
        : events.map((e) => e.eventType);
    const finalUrl = buildUrlWithEventTypes(url, effectiveEventTypes);

    console.log(`Attempting to connect ${service} SSE to:`, finalUrl);

    const es = new EventSource(finalUrl, {
      fetch: (input, init) =>
        fetch(input, {
          ...init,
          headers: { ...init?.headers, ...headers },
        }),
    });

    eventSourceRef.current = es;

    console.log(
      "Registering event listeners for types:",
      events.map((e) => e.eventType),
    );

    events.forEach(({ eventType, onEvent }) => {
      es.addEventListener(eventType, (event: MessageEvent) => {
        onEvent(JSON.parse(event.data), event);
      });
    });

    es.addEventListener("open", () => {
      console.log(`✅ ${service} SSE connection open`);
      isConnectedRef.current = true;
      onOpen?.();
    });

    es.addEventListener("error", (error: Event) => {
      console.error(`❌ ${service} SSE error:`, error);
      isConnectedRef.current = false;
      onError?.(error);
    });

    // cleanup
    return () => {
      console.log(`Closing ${service} SSE connection`);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        isConnectedRef.current = false;
      }
    };
  }, [
    url,
    enabled,
    authContext?.accessToken,
    authContext?.customerNamespace,
    eventTypeKeys,
    customHeadersKey,
    eventTypesKey,
  ]);

  return {
    /** Whether the SSE connection is currently open */
    isConnected: isConnectedRef.current,
    /** Reference to the EventSource instance (for advanced usage) */
    eventSource: eventSourceRef.current,
  };
};
